import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import { useUserDetails } from 'api/useUserDetails';
import { TemplateStudioModel } from 'common/contracts';
import { EEventsType, EQueryKeys } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { ModalActionsData } from 'store/store.types';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';
import DialogModal from 'components/DialogModal/DialogModal';
import { cn } from 'components/lib/utils';
import Typography from 'components/Typography/Typography';

import { templateStudioSchema } from '../../FormPage/validation-schema';

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

const cleanAssets = (
  template: Partial<TemplateStudioModel>
): Partial<TemplateStudioModel> => {
  const cleanedTemplate: Partial<TemplateStudioModel> = { ...template };
  // todo
  // cleanedTemplate.brandKit.logo = '';
  // cleanedTemplate.brandKit.defaultBanner = '';
  // cleanedTemplate.brandKit.backgroundImageDesktop = '';
  // cleanedTemplate.brandKit.backgroundImageMobile = '';
  // cleanedTemplate.brandKit.footerDesktopImage = '';
  // cleanedTemplate.brandKit.footerMobileImage = '';

  return cleanedTemplate;
};

export const ImportTemplateButton: React.FC = () => {
  const { publisherId } = useParams();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { userDetails } = useUserDetails();
  const queryClient = useQueryClient();
  const { createTemplate } = useTemplatesStudio(publisherId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [templateData, setTemplateData] =
    useState<Partial<TemplateStudioModel> | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const handleImportTemplate = useCallback(
    (template: Partial<TemplateStudioModel>) => {
      createTemplate.mutate(template, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [EQueryKeys.TEMPLATES_STUDIO, publisherId]
          });
          showToast({
            message: 'Template imported successfully',
            status: EToasterStatus.SUCCESS
          });
        },
        onError: (error) => {
          console.error('Import error:', error);
          showToast({
            message: 'Failed to import template',
            status: EToasterStatus.ERROR
          });
        },
        onSettled: () => {
          handleClose();
        }
      });
    },
    [createTemplate, queryClient, publisherId]
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      showToast({
        message: 'Please select a valid JSON file',
        status: EToasterStatus.ERROR
      });
      return;
    }

    try {
      const fileContent = await readFileAsText(file);
      const { template, publisherId: importedPublisherId } =
        JSON.parse(fileContent);

      // Validate the template data using Zod schema
      const validationResult = templateStudioSchema.safeParse(template);

      if (!validationResult.success) {
        const errorMessages = validationResult.error.errors
          .map((err: any) => `${err.path.join('.')}: ${err.message}`)
          .join(', ');
        throw new Error(`Template validation failed: ${errorMessages}`);
      }

      const validated =
        validationResult.data as unknown as Partial<TemplateStudioModel>;
      const templateData: Partial<TemplateStudioModel> =
        importedPublisherId !== publisherId
          ? cleanAssets(validated)
          : validated;

      setSelectedFile(file);
      setTemplateData(templateData);
    } catch (error) {
      console.error('Import error:', error);

      let errorMessage =
        'Failed to import template. Please check the file format.';

      if (error instanceof Error) {
        if (error.message.includes('Template validation failed:')) {
          errorMessage = `Template validation failed. ${error.message.replace('Template validation failed: ', '')}`;
        } else if (error.message.includes('Invalid template format')) {
          errorMessage =
            'Invalid template format. Please ensure the file contains a valid template structure.';
        } else if (error.message.includes('JSON')) {
          errorMessage =
            'Invalid JSON format. Please ensure the file contains valid JSON.';
        }
      }

      setTemplateData(null);
      setSelectedFile(null);
      setIsImporting(false);

      showToast({
        message: errorMessage,
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleImport = useCallback(async () => {
    if (!templateData) {
      showToast({
        message: 'Please select a file to import',
        status: EToasterStatus.ERROR
      });
      return;
    }

    setIsImporting(true);

    handleImportTemplate({
      ...templateData,
      templateName: (templateData.templateName ?? 'Template') + ' (imported)'
    });
  }, [templateData, handleImportTemplate]);

  const handleClose = () => {
    setTemplateData(null);
    setSelectedFile(null);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setIsImportModalOpen(false);
  };

  const modalActions: Record<string, ModalActionsData> = useMemo(
    () => ({
      onConfirm: {
        label: isImporting ? 'Importing...' : 'Import',
        action: handleImport,
        disabled: !templateData || isImporting
      },
      onClose: {
        label: 'Cancel',
        action: handleClose,
        disabled: isImporting,
        variant: 'ghost'
      }
    }),
    [templateData, isImporting, handleImport]
  );

  const handleClickOnImportButton = () => {
    setIsImportModalOpen(true);
    trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
      publisher_id: publisherId,
      user_id: userDetails?.userId,
      company_id: userDetails?.company,
      templateAction: 'import',
      isActiveTemplate: false
    });
    trackBigQueryEvent({
      eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
      publisherId: publisherId,
      userId: userDetails?.userId,
      action: 'import',
      is_active: false
    });
  };

  return (
    <>
      <Button onClick={handleClickOnImportButton} variant="outline">
        {'Import'}
      </Button>
      {isImportModalOpen && (
        <DialogModal
          isOpen={isImportModalOpen}
          title="Import Template"
          description="Select a JSON file containing a template to import into your templates library."
          modalActions={modalActions}
        >
          <div className="flex flex-col gap-4">
            <div
              className={cn(
                'flex flex-col items-center justify-center p-8 border border-dashed border-base-muted-foreground rounded-lg cursor-pointer hover:border-baseForeground transition-colors h-[190px]',
                selectedFile && 'bg-base-muted'
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              {selectedFile ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md text-center">
                  <FontAwesomeIcon
                    icon="fa-solid fa-file"
                    className="text-baseForeground mb-6"
                  />
                  <Typography className="text-sm font-medium text-baseForeground">
                    {selectedFile.name}
                  </Typography>
                  <Typography className="text-xs text-base-mutedForeground font-semibold">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </div>
              ) : (
                <>
                  <img
                    src={'./assets/icons/ac-cloud-upload.png'}
                    alt="File"
                    className="w-[48px] h-[48px] mb-7"
                  />
                  <Typography className="text-sm text-base-mutedForeground text-center">
                    {'Select a template JSON file from your computer'}
                  </Typography>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </DialogModal>
      )}
    </>
  );
};
